import { reactive, onUnmounted } from 'vue';

const SNAP_MINUTES = 15;
const MIN_DURATION = 15;
const MAX_END_MINUTES = 24 * 60;

export function useMeetingResize({ emit }) {
  const resizeState = reactive({
    resizingId: null,
  });

  let activeResize = null;

  function snapMinutes(raw) {
    return Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES;
  }

  function minutesSinceMidnight(iso) {
    const d = new Date(iso);
    return d.getHours() * 60 + d.getMinutes();
  }

  function withMinutes(originalIso, minutes) {
    const d = new Date(originalIso);
    d.setHours(0, minutes, 0, 0);
    return d;
  }

  function onMousemove(event) {
    if (!activeResize) return;

    const deltaY = event.clientY - activeResize.startY;
    const rawMinutes = activeResize.originalMinutes + deltaY;
    const snapped = snapMinutes(rawMinutes);

    if (activeResize.edge === 'end') {
      const minEnd = activeResize.startMinutes + MIN_DURATION;
      activeResize.currentMinutes = Math.max(
        minEnd,
        Math.min(snapped, MAX_END_MINUTES)
      );
    } else {
      const maxStart = activeResize.endMinutes - MIN_DURATION;
      activeResize.currentMinutes = Math.max(0, Math.min(snapped, maxStart));
    }

    activeResize.onPreview?.(activeResize.currentMinutes);
  }

  function onMouseup() {
    if (!activeResize) return;

    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    resizeState.resizingId = null;

    const { meeting, edge, currentMinutes, day, onPreviewEnd } = activeResize;
    activeResize = null;
    onPreviewEnd?.();

    if (currentMinutes === null) return;

    const start = new Date(meeting.start_at);
    const end = new Date(meeting.end_at);

    let newStart = start;
    let newEnd = end;

    if (edge === 'end') {
      newEnd = withMinutes(day, currentMinutes);
    } else {
      newStart = withMinutes(day, currentMinutes);
    }

    if (
      newStart.getTime() === start.getTime() &&
      newEnd.getTime() === end.getTime()
    ) {
      return;
    }

    emit('meetingDrop', {
      id: meeting.id,
      start_at: newStart.toISOString(),
      end_at: newEnd.toISOString(),
      originalMeeting: meeting,
    });
  }

  function onResizeMousedown(event, meeting, day, edge, cardEl) {
    event.preventDefault();
    event.stopPropagation();

    if (activeResize) {
      document.removeEventListener('mousemove', onMousemove);
      document.removeEventListener('mouseup', onMouseup);
    }

    const startMinutes = minutesSinceMidnight(meeting.start_at);
    const endMinutes = minutesSinceMidnight(meeting.end_at);

    activeResize = {
      meeting,
      day,
      edge,
      startY: event.clientY,
      originalMinutes: edge === 'end' ? endMinutes : startMinutes,
      startMinutes,
      endMinutes,
      currentMinutes: edge === 'end' ? endMinutes : startMinutes,
      onPreview: minutes => {
        if (!cardEl) return;
        if (edge === 'end') {
          cardEl.style.height = `${Math.max(15, minutes - startMinutes)}px`;
        } else {
          cardEl.style.top = `${minutes}px`;
          cardEl.style.height = `${Math.max(15, endMinutes - minutes)}px`;
        }
      },
      onPreviewEnd: () => {
        if (!cardEl) return;
        cardEl.style.top = '';
        cardEl.style.height = '';
      },
    };

    resizeState.resizingId = meeting.id;
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';

    document.addEventListener('mousemove', onMousemove);
    document.addEventListener('mouseup', onMouseup);
  }

  onUnmounted(() => {
    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    activeResize = null;
  });

  return { onResizeMousedown, resizeState };
}
